import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';

// ── One-click encrypted backups (insuredocs-tarbackup) ───────────────────────
// Rendered inside BackupStatusModal, below the restic rows. Admin-only (the modal is).
// Backend: /api/backup/tar/{status,history,start,cancel,retry-upload} (routers/backup.py, 5.8.0).
// Design: tarbackup design v1 §6, v2 §3 / §7.1 / §7.5, amended by DECISIONS.md 2026-10-03
// (no business-hours rule, single-click confirm, no TEST button; 5.8.0: FULL / Code only × QNAP / UNAS / Both).
// The backend + engine decide everything; this panel only shows the status file and sends the confirm word.
// Retention numbers come from the server (history.modes[mode].retention) — never hard-coded here.
// 5.25.0: Mattermost CT 1017 (mode "mm", confirm MATTERMOST, history under history.apps.mattermost).
// One status / result box for both apps (one engine lock → one job at a time); an InsureDocs | Mattermost
// switch picks which buttons and history show (remembered per browser).
// 5.29.0: Timeclock VM 10.0.9.14 (mode "tc", confirm TIMECLOCK, history under history.apps.timeclock) — a third
// switch position, same buttons / confirm / progress / cancel / retry / history as Mattermost. No blackout windows:
// the engine refuses while timeclock-vm's own nightly backup runs, and that refusal shows in the result box.
// 5.35.0: HDCal 10.0.9.9 (mode "cal", confirm HDCAL, history under history.apps.hdcal) — a fourth switch position,
// same buttons / confirm / progress / cancel / retry / history. The engine refuses while HDCal reports busy (another
// export running there) or while the 23:00 nightly HDCal pull is due/running.

const LABEL = { full: 'FULL', nodv: 'NO-DOCSVAULT', mm: 'MATTERMOST', tc: 'TIMECLOCK', cal: 'HDCAL' };   // confirm word sent to the backend
const MODE_NAME = { full: 'FULL', nodv: 'Code only', mm: 'FULL', tc: 'FULL', cal: 'FULL' };        // what Henry sees (in its app's section)
const MODE_TITLE = { full: 'FULL', nodv: 'Code only', mm: 'Mattermost FULL', tc: 'Timeclock FULL', cal: 'HDCal FULL' };   // stand-alone (status, confirm)
const MODE_ICON = { full: '🗄 ', nodv: '💾 ', mm: '💬 ', tc: '⏱ ', cal: '📅 ' };
const MODE_SUB = {
  full: 'everything, incl. the Docsvault documents',
  nodv: 'all databases + apps + settings — no Docsvault documents',
  mm: 'program + config + data + plugins + database (CT 1017) — Mattermost stays up',
  tc: 'TimeClock + TimeClockSol (databases + apps) + Office IP map + server settings (timeclock-vm) — the apps stay up',
  cal: 'Davis calendars + contacts (database) + app + server settings (HDCal) — the calendar stays up',
};
const APPS = {
  insuredocs: {
    name: 'InsureDocs', modes: ['full', 'nodv'],
    qnap: 'QNAP: Server Backup/InsureDocs_Manual_Backups', unas: 'UNAS: InsureDocs Backup/Manual Backups',
  },
  mattermost: {
    name: 'Mattermost', modes: ['mm'],
    qnap: 'QNAP: Server Backup/Mattermost_Manual_Backups', unas: 'UNAS: Mattermost Backup/Manual Backups',
  },
  timeclock: {
    name: 'Timeclock', modes: ['tc'],
    qnap: 'QNAP: Server Backup/Timeclock_Manual_Backups', unas: 'UNAS: Timeclock Backup/Manual Backups',
  },
  hdcal: {
    name: 'HDCal', modes: ['cal'],
    qnap: 'QNAP: Server Backup/HDCal_Manual_Backups', unas: 'UNAS: HDCal Backup/Manual Backups',
  },
};
const APP_ORDER = ['insuredocs', 'mattermost', 'timeclock', 'hdcal'];
const APP_STORE_KEY = 'insuredocs.tarbackup.app';
const isMm = (st) => !!st && (st.app === 'mattermost' || st.mode === 'mm');
const isTc = (st) => !!st && (st.app === 'timeclock' || st.mode === 'tc');
const isCal = (st) => !!st && (st.app === 'hdcal' || st.mode === 'cal');
const appOf = (st) => (isMm(st) ? 'mattermost' : isTc(st) ? 'timeclock' : isCal(st) ? 'hdcal' : 'insuredocs');
const appOfMode = (m) => (m === 'mm' ? 'mattermost' : m === 'tc' ? 'timeclock' : m === 'cal' ? 'hdcal' : 'insuredocs');
const SOURCE = { mm: 'CT 1017', tc: 'timeclock-vm', cal: 'HDCal' };   // where the remote apps' bundles are pulled from
const remoteKey = (st) => (isTc(st) ? 'tc' : isCal(st) ? 'cal' : 'mm');
const runIcon = (st) => (isMm(st) ? MODE_ICON.mm : isTc(st) ? MODE_ICON.tc : isCal(st) ? MODE_ICON.cal : '');
const runTitle = (st) => (isMm(st) ? MODE_TITLE.mm : isTc(st) ? MODE_TITLE.tc : isCal(st) ? MODE_TITLE.cal
  : st?.mode_label === 'NO-DOCSVAULT' ? MODE_NAME.nodv : (st?.mode_label || ''));
const loadApp = () => {
  try { const v = window.localStorage.getItem(APP_STORE_KEY); return APPS[v] ? v : 'insuredocs'; } catch { return 'insuredocs'; }
};
const saveApp = (v) => { try { window.localStorage.setItem(APP_STORE_KEY, v); } catch { /* private window etc. */ } };
const DESTS = ['qnap', 'unas', 'both'];
const DEST_LABEL = { qnap: '→ QNAP', unas: '→ UNAS', both: '→ Both' };
const DEST_WORDS = { qnap: 'QNAP only (in the office)', unas: 'UNAS only (off-site)', both: 'both QNAP and UNAS' };
const TZ = 'America/Chicago';
const POLL_RUNNING_MS = 3000;
const POLL_IDLE_MS = 30000;
const TERMINAL = ['done', 'failed', 'cancelled'];

const PHASE_TEXT = {
  preflight: 'Checking preconditions', estimating: 'Estimating size', dumping: 'Dumping databases',
  fetching: 'Fetching the bundle from CT 1017', validating: 'Checking the bundle',
  archiving: 'Archiving + encrypting → QNAP', checking: 'Checking the archive', testing: 'Deep test (decrypt + list)',
  hashing: 'Checking the file (checksum)',
  uploading: 'Uploading to UNAS', verifying: 'Verifying the UNAS copy', pruning: 'Pruning old sets',
  done: 'Done', failed: 'Failed', cancelled: 'Cancelled',
};
const phaseText = (phase, st) => (phase === 'fetching' && isTc(st) ? 'Fetching the export from timeclock-vm'
  : phase === 'fetching' && isCal(st) ? 'Fetching the export from HDCal'
    : (PHASE_TEXT[phase] || phase));
const STEP_TEXT = {
  preflight: 'preflight', dumping: 'dumps', estimating: 'estimate', fetching: 'fetch', validating: 'check bundle',
  archiving: 'archive', checking: 'gate',
  testing: 'deep test', hashing: 'checksum', uploading: 'upload', verifying: 'verify', pruning: 'prune',
};

// v2 §7.1 table: what a cancel keeps / removes, by phase (+ engine 1.1 destination rules).
const cancelEffect = (phase, action, dest, app) => {
  if (action === 'retry-upload') return 'The QNAP copy is untouched; the UNAS partial copy is deleted.';
  if (dest === 'qnap' && !['preflight', 'estimating', 'dumping', 'fetching', 'validating'].includes(phase)) {
    return 'QNAP only: the unfinished archive is deleted (a QNAP-only set is never kept half-done). Nothing is written except the .log.';
  }
  const where = dest === 'unas' ? 'The temporary QNAP copy' : 'The QNAP .7z';
  switch (phase) {
    case 'preflight': case 'estimating': case 'dumping':
      return 'The database staging is removed. Nothing is written except the .log.';
    case 'fetching': case 'validating':
      if (app === 'timeclock') return 'The local copy of the Timeclock export is deleted (timeclock-vm cleans up its own temp files). Nothing is written except the .log.';
      if (app === 'hdcal') return 'The local copy of the HDCal export is deleted (HDCal cleans up its own temp files). Nothing is written except the .log.';
      return 'The local copy of the Mattermost bundle is deleted (CT 1017 cleans up its own temp files). Nothing is written except the .log.';
    case 'archiving': case 'checking':
      return 'The unfinished .7z.partial and the staging are removed. Nothing is written except the .log.';
    case 'uploading': case 'testing':
      return `${where} is KEPT but unhashed (no .7z.sha256). The UNAS partial copy is deleted. Use Retry upload later, or delete it.`;
    case 'verifying':
      return `${where} and its .7z.sha256 are kept (complete). The UNAS partial copy is deleted; UNAS is not verified.`;
    default:
      return 'The job stops now; the cleanup trap removes anything unfinished.';
  }
};

// ── formatting ────────────────────────────────────────────────────────────────
const gib = (b) => {
  if (b === null || b === undefined || isNaN(b)) return '—';
  const g = b / 1073741824;
  return `${g.toFixed(g < 10 ? 2 : 1)} GiB`;
};
const mibs = (bps) => (bps ? `${(bps / 1048576).toFixed(1)} MiB/s` : null);
const dur = (s) => {
  if (s === null || s === undefined || isNaN(s)) return null;
  s = Math.max(0, Math.round(s));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const durWords = (s) => {
  if (!s && s !== 0) return null;
  const h = Math.floor(s / 3600), m = Math.max(1, Math.round((s % 3600) / 60));
  return h ? `${h} h ${m} min` : `${m} min`;
};
const hhmm = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleTimeString('en-US', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
};
const dateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};
const setDate = (name) => {     // 2026-10-02_2200_InsureDocs_… → 2026-10-02 22:00 (names are Central time)
  const m = /^(\d{4}-\d{2}-\d{2})_(\d{2})(\d{2})_/.exec(name || '');
  return m ? `${m[1]} ${m[2]}:${m[3]}` : name;
};
const shortSha = (s) => (s && s.length >= 8 ? `${s.slice(0, 4)}…${s.slice(-4)}` : '—');
const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join('; ');
  return err?.message || 'Request failed';
};
const isRefresh = (msg) => /refresh the page/i.test(msg || '');

// ── presentational pieces (module level so a 3 s poll never remounts them) ────
function Bar({ ui, label, pct, indeterminate, right, thin }) {
  return (
    <div style={{ marginTop: thin ? 6 : 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: thin ? 11 : 12, marginBottom: 3 }}>
        <span style={{ fontWeight: thin ? 400 : 600 }}>{label}</span>
        <span style={{ color: ui.textSecondary }}>{right}</span>
      </div>
      <div style={{ height: thin ? 4 : 10, background: ui.barBg, borderRadius: 5, overflow: 'hidden', position: 'relative' }}>
        {indeterminate
          ? <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg, #5d7a96 0 10px, #34495e 10px 20px)', opacity: 0.6 }} />
          : <div style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, height: '100%', background: '#2e86c1', transition: 'width 0.6s' }} />}
      </div>
    </div>
  );
}

function Box({ ui, kind, children }) {
  return <div style={{ ...ui.box[kind], borderRadius: 8, padding: '10px 12px', fontSize: 12, marginTop: 10 }}>{children}</div>;
}

function ConfirmPanel({ ui, title, children, okText, onOk, onCancel, busy }) {
  return (
    <div style={{ background: ui.subBg, border: `1px solid ${ui.borderColor}`, borderRadius: 8, padding: '12px 14px', marginTop: 10, fontSize: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{title}</div>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button style={ui.btn(false, busy)} disabled={busy} onClick={onCancel}>Cancel</button>
        <button style={ui.btn(true, busy)} disabled={busy} onClick={onOk}>{busy ? 'Sending…' : okText}</button>
      </div>
    </div>
  );
}

export default function TarBackupPanel({ darkMode }) {
  const [resp, setResp] = useState(null);          // GET /tar/status
  const [statusErr, setStatusErr] = useState(null);
  const [hist, setHist] = useState(null);          // GET /tar/history
  const [histErr, setHistErr] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);    // {kind:'start',mode,dest} | {kind:'cancel'} | {kind:'retry',name,mode}
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);
  const [starting, setStarting] = useState(null);  // {rid, at} after a 202, until the status file shows that run
  const [app, setAppState] = useState(loadApp);     // which app's buttons + history show
  const setApp = (v) => { setAppState(v); saveApp(v); setConfirm(c => (c?.kind === 'start' ? null : c)); };
  const followed = useRef(null);                    // run (request_id) we already switched the view to

  const mounted = useRef(true);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const panelBg = darkMode ? '#242424' : '#f5f5f5';
  const subBg = darkMode ? '#1f1f1f' : '#eeeeee';
  const borderColor = darkMode ? '#333' : '#ddd';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const barBg = darkMode ? '#333' : '#ddd';
  const box = {
    green: { background: darkMode ? '#1e2b14' : '#EAF3DE', color: darkMode ? '#a8d38a' : '#3B6D11', border: '1px solid #3B6D11' },
    amber: { background: darkMode ? '#2e2512' : '#FBF0D9', color: darkMode ? '#e8c27a' : '#7A5200', border: '1px solid #BA7517' },
    red: { background: darkMode ? '#2e1717' : '#FCEBEB', color: darkMode ? '#f0a0a0' : '#A32D2D', border: '1px solid #A32D2D' },
    grey: { background: subBg, color: textSecondary, border: `1px solid ${borderColor}` },
  };
  const btn = (primary, disabled) => ({
    padding: '7px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    border: primary ? 'none' : `1px solid ${borderColor}`, opacity: disabled ? 0.5 : 1,
    background: primary ? '#34495e' : 'transparent', color: primary ? '#fff' : (darkMode ? '#f0f0f0' : '#1a1a1a'),
  });
  const ui = { subBg, borderColor, textSecondary, barBg, box, btn };

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const r = await api.get('/backup/tar/history');
      if (mounted.current) { setHist(r.data); setHistErr(null); }
    } catch (err) {
      if (mounted.current) setHistErr(errText(err));
    } finally {
      if (mounted.current) setHistLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/backup/tar/status');
      if (!mounted.current) return null;
      setResp(r.data); setStatusErr(null);
      return r.data;
    } catch (err) {
      if (mounted.current) setStatusErr(errText(err));
      return null;
    }
  }, []);

  // Poll loop: 3 s while a run is active (or we just started one), 30 s when idle. Stops on unmount.
  const startingRef = useRef(null);
  startingRef.current = starting;
  const tick = useCallback(async () => {
    clearTimeout(timer.current);
    const d = await loadStatus();
    if (!mounted.current) return;
    const st = d?.status;
    let pending = startingRef.current;
    if (pending && (st?.request_id === pending.rid || Date.now() - pending.at > 60000)) {
      pending = null; setStarting(null);
    }
    const running = !!d?.running || !!pending;
    if (wasRunning.current && !running) loadHistory();       // a run just finished
    wasRunning.current = running;
    timer.current = setTimeout(tick, running ? POLL_RUNNING_MS : POLL_IDLE_MS);
  }, [loadStatus, loadHistory]);

  useEffect(() => {
    mounted.current = true;
    tick();
    loadHistory();
    return () => { mounted.current = false; clearTimeout(timer.current); };
  }, [tick, loadHistory]);

  const post = async (url, body, after) => {
    setBusy(true); setActionErr(null);
    try {
      const r = await api.post(url, body);
      setConfirm(null);
      if (after) after(r.data);
      tick();
    } catch (err) {
      setActionErr(errText(err));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };
  const doStart = (mode, dest) => post('/backup/tar/start', { mode, dest, confirm: LABEL[mode] },
    (d) => setStarting({ rid: d.request_id, at: Date.now() }));
  const doCancel = () => post('/backup/tar/cancel', { confirm: 'CANCEL' });
  const doRetry = (name) => post('/backup/tar/retry-upload', { name, confirm: 'RETRY' },
    (d) => setStarting({ rid: d.request_id, at: Date.now() }));

  // ── derived ────────────────────────────────────────────────────────────────
  const st = resp?.status || null;
  const running = !!resp?.running || !!starting;
  const stTerminal = st && (TERMINAL.includes(st.phase) || st.result);
  const showLive = running && st && !stTerminal && (!starting || st.request_id === starting.rid);
  const modeHist = (m) => (m === 'mm' ? hist?.apps?.mattermost?.modes?.mm
    : m === 'tc' ? hist?.apps?.timeclock?.modes?.tc : m === 'cal' ? hist?.apps?.hdcal?.modes?.cal : hist?.modes?.[m]);
  const appHist = (a) => (a === 'mattermost' ? hist?.apps?.mattermost : a === 'timeclock' ? hist?.apps?.timeclock
    : a === 'hdcal' ? hist?.apps?.hdcal : hist);
  const blackout = resp?.mm_blackout || hist?.apps?.mattermost?.blackout || null;

  // A running Mattermost job (or InsureDocs job) shows its own app's section — once per run.
  useEffect(() => {
    const s = resp?.status;
    if (!resp?.running || !s?.request_id || followed.current === s.request_id) return;
    followed.current = s.request_id;
    const a = appOf(s);
    if (a !== app) { setAppState(a); }
  }, [resp, app]);

  const sizeLine = (mode) => {
    const lr = modeHist(mode)?.last_run;
    if (!lr) return '…';
    return `${gib(lr.inner_bytes)} → ${gib(lr.archive_bytes)} .7z ${lr.estimate ? '(estimate)' : '(last run)'}`;
  };

  // Estimated time: archive (from the last run, minus its upload) + upload/verify (UNAS/Both) from the measured rate.
  const timeEstimate = (mode, dest) => {
    const lr = modeHist(mode)?.last_run || {};
    const rate = lr.upload_rate_bps;
    const xfer = rate && lr.archive_bytes ? (2 * lr.archive_bytes) / rate : null;     // upload + verify read-back
    const archive = lr.duration_seconds ? Math.max(60, lr.duration_seconds - (xfer || 0)) : null;
    const parts = [archive ? `archive ~${durWords(archive)}` : 'archive time unknown until the first run'];
    if (dest === 'qnap') parts.push('+ a local checksum read');
    else parts.push(xfer ? `+ upload + verify ~${durWords(xfer)} (at ${mibs(rate)})` : '+ upload + verify: unknown until the first upload');
    return `${parts.join(' ')} — estimate`;
  };

  const retentionLines = (mode, dest) => {
    const r = modeHist(mode)?.retention;
    if (!r) return [<div key="none">Retention: not known yet (history still loading).</div>];
    const side = (key, label) => {
      const s = r[key] || {};
      const head = `${label}: keeps the newest ${r.keep} ${MODE_TITLE[mode]}`;
      if (!s.available) {
        return (
          <div key={key} style={{ marginTop: 4, color: box.amber.color }}>
            ⚠ {head}. {key === 'unas' ? 'The UNAS list is not available yet' : 'The QNAP folder is not readable right now'} — what will be deleted is unknown.
          </div>
        );
      }
      const asOf = key === 'unas' ? ` (UNAS list as of ${dateTime(s.as_of)})` : '';
      if (r.keep === 0) return <div key={key} style={{ marginTop: 4 }}>{label}: retention is off — nothing will be deleted{asOf}.</div>;
      const del = s.would_delete_if_new || [];
      return (
        <div key={key} style={{ marginTop: 4 }}>
          {head}{asOf}.{' '}
          {del.length
            ? <>After the new copy is checked, <b>{del.join(', ')}</b> will be deleted (at most {r.max_per_run} per run).</>
            : `Nothing will be deleted (${(s.complete || []).length} kept now).`}
        </div>
      );
    };
    const out = [];
    if (dest === 'qnap' || dest === 'both') out.push(side('qnap', 'QNAP'));
    if (dest === 'unas' || dest === 'both') out.push(side('unas', 'UNAS'));
    return out;
  };

  // ── running view ───────────────────────────────────────────────────────────
  const runningView = () => {
    if (!showLive) {
      return (
        <div style={{ background: panelBg, borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>A backup is running</div>
          <div style={{ color: textSecondary, marginTop: 4 }}>
            {starting ? 'Starting… waiting for the job to report.' : `${resp?.active_unit || 'tar backup'} — waiting for its status.`}
          </div>
          <Bar ui={ui} label="Starting" indeterminate right="" />
        </div>
      );
    }
    const a = st.archive || {}, u = st.upload || {}, t = st.test || {}, hs = st.hash || {};
    const phase = st.phase;
    const retry = st.action === 'retry-upload';
    const dest = resp.dest || st.dest || 'both';
    const qnapOnly = dest === 'qnap';
    const mmRun = isMm(st);
    const remoteRun = mmRun || isTc(st) || isCal(st); // the bundle is pulled over ssh first (fetch + check steps)
    const src = SOURCE[remoteKey(st)];
    const fe = st.fetch || {};
    const early = ['preflight', 'estimating', 'dumping', 'fetching', 'validating'].includes(phase);
    const afterArchive = !early && phase !== 'archiving';
    const aPct = afterArchive ? 100 : (a.bytes_total ? (100 * (a.bytes_done || 0)) / a.bytes_total : null);
    const uTotal = u.bytes_total || 0;
    const uDone = (u.bytes_done || 0) + (u.verify_bytes_done || 0);
    const uPct = uTotal ? (100 * uDone) / (2 * uTotal) : 0;
    const verifying = phase === 'verifying';
    const uRate = verifying ? u.verify_rate_bps : u.rate_bps;
    const uEta = verifying ? u.verify_eta_seconds : u.eta_seconds;
    const hTotal = hs.bytes_total || 0;
    const hPct = hTotal ? (100 * (hs.bytes_done || 0)) / hTotal : (['pruning'].includes(phase) ? 100 : 0);
    const steps = retry
      ? ['preflight', 'uploading', 'verifying', 'pruning']
      : ['preflight', ...(remoteRun ? ['fetching', 'validating'] : ['dumping', 'estimating']), 'archiving', 'checking',
        ...(st.test_sequential ? ['testing'] : []), ...(qnapOnly ? ['hashing'] : ['uploading', 'verifying']), 'pruning'];
    const fTotal = fe.bytes_total || 0;
    const fDone = phase === 'fetching' ? (fe.bytes_done || 0) : (['preflight'].includes(phase) ? 0 : (fe.bytes || fTotal));
    const fPct = fTotal ? (100 * fDone) / fTotal : (phase === 'fetching' ? null : 0);
    const cur = steps.indexOf(phase);
    const cancelOk = resp.cancel_allowed && !resp.is_test;
    // stream.py's overall % assumes an upload; for QNAP-only runs show step-based progress instead
    const overallTxt = qnapOnly
      ? (cur >= 0 ? `step ${cur + 1} of ${steps.length}` : '')
      : (st.overall?.pct != null ? `${st.overall.pct.toFixed(0)}%${st.overall.eta_seconds ? ` · ETA ${dur(st.overall.eta_seconds)}` : ''}` : '');
    return (
      <div style={{ background: panelBg, borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {resp.is_test && <span style={{ background: '#7d3c98', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: 10, marginRight: 6 }}>TEST</span>}
            {runIcon(st)}{runTitle(st)}{st.mm_version ? ` v${st.mm_version}` : ''}{st.cal_version ? ` (Davis v${st.cal_version})` : ''} {DEST_LABEL[dest] || ''}{retry ? ' retry upload' : ''} · started {hhmm(st.started_at)} by {st.who?.username || '?'} · phase: {phaseText(phase, st)}
          </div>
          {overallTxt && <span style={{ color: textSecondary }}>{overallTxt}</span>}
        </div>

        {!retry && remoteRun && (
          <Bar ui={ui} label={`Fetch ← ${src} (local staging)`} indeterminate={phase === 'fetching' && fPct === null} pct={fPct}
            right={phase === 'preflight' ? 'waiting'
              : phase === 'fetching'
                ? `${gib(fe.bytes_done)} / ~${gib(fTotal)}${mibs(fe.rate_bps) ? ` · ${mibs(fe.rate_bps)}` : ''}${fe.eta_seconds ? ` · ETA ${dur(fe.eta_seconds)}` : ''}`
                : phase === 'validating' ? `${gib(fe.bytes || fDone)} · checking the bundle` : `${gib(fe.bytes || fDone)} ✓`} />
        )}
        {!retry && (
          <Bar ui={ui} label={dest === 'unas' ? 'Archive → QNAP (temporary)' : 'Archive → QNAP'} indeterminate={early || aPct === null} pct={aPct}
            right={early ? phaseText(phase, st) : `${gib(afterArchive ? (a.inner_bytes || a.bytes_total) : a.bytes_done)} / ${gib(a.inner_bytes || a.bytes_total)}${!afterArchive && mibs(a.rate_bps) ? ` · ${mibs(a.rate_bps)}` : ''}${!afterArchive && a.eta_seconds ? ` · ETA ${dur(a.eta_seconds)}` : ''}`} />
        )}
        {qnapOnly ? (
          <Bar ui={ui} label="Checksum (QNAP)" pct={hPct}
            right={hTotal ? `${gib(hs.bytes_done)} / ${gib(hTotal)}${mibs(hs.rate_bps) ? ` · ${mibs(hs.rate_bps)}` : ''}` : (phase === 'pruning' ? 'done' : 'waiting')} />
        ) : (
          <Bar ui={ui} label="Copy → UNAS" pct={uPct}
            right={uTotal
              ? `${verifying ? 'verifying' : 'uploading'} ${gib(verifying ? u.verify_bytes_done : u.bytes_done)} / ${gib(uTotal)}${mibs(uRate) ? ` · ${mibs(uRate)}` : ''}${uEta ? ` · ETA ${dur(uEta)}` : ''}${u.attempt > 1 ? ` · attempt ${u.attempt}/${u.attempts}` : ''}`
              : 'waiting'} />
        )}
        {t.bytes_done != null && t.bytes_total > 0 && (
          <Bar ui={ui} thin label="Test (decrypt + list)" pct={(100 * t.bytes_done) / t.bytes_total}
            right={t.ok === true ? '✓ ok' : t.ok === false ? '✗ failed' : `${((100 * t.bytes_done) / t.bytes_total).toFixed(0)}%`} />
        )}

        <div style={{ marginTop: 10, color: textSecondary }}>
          {steps.map((s, i) => (
            <span key={s} style={{ marginRight: 10, color: i === cur ? (darkMode ? '#f0f0f0' : '#1a1a1a') : textSecondary, fontWeight: i === cur ? 600 : 400 }}>
              {i < cur ? '✓' : i === cur ? '●' : '○'} {STEP_TEXT[s]}
            </span>
          ))}
          {!st.test_sequential && !retry && t.bytes_total > 0 && (
            <span style={{ marginRight: 10 }}>{t.ok === true ? '✓' : t.ok === false ? '✗' : '●'} deep test (parallel)</span>
          )}
        </div>

        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={btn(false, !cancelOk || busy)} disabled={!cancelOk || busy}
            onClick={() => { setActionErr(null); setConfirm({ kind: 'cancel' }); }}>Cancel backup</button>
          <span style={{ color: textSecondary }}>
            {resp.is_test ? 'CLI test run — stop it from the terminal.' : !resp.cancel_allowed ? (phase === 'pruning' ? 'Pruning — finishes in seconds, cannot cancel.' : 'Cancel not available right now.') : ''}
          </span>
        </div>
        {confirm?.kind === 'cancel' && (
          <ConfirmPanel ui={ui} busy={busy} onCancel={() => setConfirm(null)} title={`Cancel this ${retry ? 'retry upload' : 'backup'}?`} okText="Cancel backup now" onOk={doCancel}>
            <div>Stops now. Phase <b>{phaseText(phase, st)}</b>: {cancelEffect(phase, st.action, dest, appOf(st))}</div>
            <div style={{ marginTop: 4 }}>Nothing is pruned after a cancel.</div>
          </ConfirmPanel>
        )}
      </div>
    );
  };

  // ── result box (latest run, whatever started it) ───────────────────────────
  const resultBox = () => {
    if (!st || !stTerminal) return null;
    const a = st.archive || {}, u = st.upload || {}, t = st.test || {}, pr = st.prune || {};
    const dest = resp.dest || st.dest || 'both';
    const tag = resp.is_test ? '[TEST] ' : '';
    const modeTxt = `${runIcon(st)}${runTitle(st)}${st.mm_version ? ` v${st.mm_version}` : ''}${st.cal_version ? ` (Davis v${st.cal_version})` : ''}`;
    const what = `${tag}${modeTxt} ${DEST_LABEL[dest] || ''}${st.action === 'retry-upload' ? ' retry upload' : ''}`;
    const errLine = st.error?.message ? <div style={{ marginTop: 4 }}>{st.error.phase ? `at ${st.error.phase}: ` : ''}{st.error.message}</div> : null;
    const logLine = (
      <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
        {st.log_path ? <>log: {st.log_path}<br /></> : null}diagnose: journalctl -u {st.unit} -n 80
      </div>
    );
    const eligible = !resp.is_test && st.mode && modeHist(st.mode)?.retry_eligible;
    const retryBtn = eligible && !running && (
      <div style={{ marginTop: 8 }}>
        <button style={btn(true, busy)} disabled={busy}
          onClick={() => { setActionErr(null); setConfirm({ kind: 'retry', name: eligible, mode: st.mode }); }}>🔁 Retry upload to UNAS</button>
      </div>
    );
    if (resp.stale) {
      return <Box ui={ui} kind="red"><b>{what} — job stopped unexpectedly</b>{errLine}{logLine}</Box>;
    }
    const whereOk = dest === 'qnap' ? 'QNAP ✓' : dest === 'unas' ? `UNAS ${u.verified ? '✓' : '✗'}` : `QNAP ✓ UNAS ${u.verified ? '✓' : '✗'}`;
    switch (st.result) {
      case 'ok':
        return (
          <Box ui={ui} kind="green">
            <b>✓ {what} · {hhmm(st.finished_at)}</b> · {gib(a.archive_bytes)} · sha256 {shortSha(a.archive_sha256)} · {whereOk}
            {' · '}read-through {t.ok ? `✓${t.members ? ` (${Number(t.members).toLocaleString()} members)` : ''}` : '—'}
            {a.pw_label ? ` · 🔒 ${a.pw_label}` : ''} · pruned QNAP {(pr.qnap_deleted || []).length} / UNAS {(pr.unas_deleted || []).length}
            {st.name && <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 11 }}>{st.name}</div>}
          </Box>
        );
      case 'partial':
        return <Box ui={ui} kind="amber"><b>⚠ {what} PARTIAL · {hhmm(st.finished_at)} — the archive is kept on the QNAP{dest === 'unas' ? ' (temporary folder)' : ''}, the UNAS copy failed</b>{errLine}<div style={{ marginTop: 4 }}>Nothing was pruned.</div>{logLine}{retryBtn}</Box>;
      case 'cancelled':
        return <Box ui={ui} kind="grey"><b>⏹ {what} cancelled{st.cancelled_by ? ` by ${st.cancelled_by}` : ''} · {hhmm(st.finished_at)}</b>{errLine}{logLine}{retryBtn}</Box>;
      case 'refused':
        return <Box ui={ui} kind="amber"><b>⚠ {what} refused · {hhmm(st.finished_at)}</b>{errLine}{logLine}</Box>;
      default:
        return <Box ui={ui} kind="red"><b>🛑 {what} FAILED · {hhmm(st.finished_at)}</b>{errLine}{logLine}{retryBtn}</Box>;
    }
  };

  // ── history ────────────────────────────────────────────────────────────────
  const th = { textAlign: 'left', fontWeight: 600, padding: '4px 6px', borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap' };
  const td = { padding: '4px 6px', borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap', verticalAlign: 'top' };
  const stateNote = {
    unhashed: 'unhashed — retry upload or delete', deeptest_failed: 'read-through FAILED — not restorable, kept for inspection',
    in_progress: 'in progress / unfinished', log_only: 'no archive (log only)', unas_incomplete: 'UNAS copy without its checksum file',
  };
  const whereCell = (r) => {
    const parts = [];
    if (r.qnap.present) parts.push(r.qnap.complete ? 'QNAP ✓' : 'QNAP ⚠');
    if (r.unas.present) parts.push(r.unas.verified ? 'UNAS ✓' : 'UNAS ⚠');
    if (r.staging) parts.push('waiting for retry');
    return parts.length ? parts.join(' · ') : '—';
  };
  const checksCell = (r) => {
    const read = r.deep_test_ok === true ? 'read ✓' : r.deep_test_failed === true ? 'read ✗'
      : (r.deep_test_ok === null || r.deep_test_ok === undefined) ? 'read: unknown' : 'read —';
    const parts = [read];
    if (r.unas.present) parts.push(r.unas.verified ? 'UNAS verified ✓' : 'UNAS not verified');
    if (r.unas.sha_match === true) parts.push('checksums match ✓');
    else if (r.unas.sha_match === false) parts.push('checksums DIFFER ✗');
    if (r.unas.present && r.unas.log === null) parts.push('log uploading');
    return parts.join(' · ');
  };
  const historyTable = (mode) => {
    const mh = modeHist(mode);
    const rows = mh?.sets || [];
    const legacy = mode === 'full' ? (hist?.legacy || []) : [];      // InsureDocs only
    const ret = mh?.retention;
    return (
      <div key={mode} style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          {MODE_ICON[mode]}{MODE_TITLE[mode]}
          {ret && <span style={{ color: textSecondary, fontWeight: 400 }}> · keeps the newest {ret.keep} on each NAS (separately)</span>}
        </div>
        {rows.length === 0 && legacy.length === 0
          ? <div style={{ fontSize: 12, color: textSecondary }}>No sets yet.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr>
                  <th style={th}>When</th><th style={th}>Size</th><th style={th}>Where</th><th style={th}>Checks</th>
                  <th style={th}>Result</th><th style={th}>By</th><th style={th}>Duration</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const warn = r.state !== 'complete';
                    const rowStyle = warn ? { background: box.amber.background, color: box.amber.color } : undefined;
                    return (
                      <React.Fragment key={r.name}>
                        <tr style={rowStyle} title={r.version ? `${r.name} · v${r.version}` : r.name}>
                          <td style={td}>{setDate(r.name)}</td>
                          <td style={td}>{gib(r.bytes)}</td>
                          <td style={td}>{whereCell(r)}</td>
                          <td style={td}>{checksCell(r)}</td>
                          <td style={td}>{r.result || ''}</td>
                          <td style={td}>{r.who || ''}</td>
                          <td style={td}>{dur(r.duration_seconds) || ''}</td>
                        </tr>
                        {(warn || r.retry_eligible) && (
                          <tr style={rowStyle}>
                            <td style={{ ...td, paddingTop: 0 }} colSpan={7}>
                              {warn && <>⚠ {stateNote[r.state] || r.state}</>}
                              {r.retry_eligible && !running && (
                                <button style={{ ...btn(false, busy), marginLeft: warn ? 8 : 0, padding: '2px 8px', fontSize: 11 }} disabled={busy}
                                  onClick={() => { setActionErr(null); setConfirm({ kind: 'retry', name: r.name, mode }); }}>🔁 Retry upload to UNAS</button>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {legacy.map(l => (
                    <tr key={l.name} style={{ color: textSecondary, opacity: 0.7 }} title={l.name}>
                      <td style={td}>{l.name.replace(/^insuredocs_MANUAL_FULL_/, '').replace(/\.tar$/, '')}</td>
                      <td style={td}>{gib(l.bytes)}</td>
                      <td style={td} colSpan={5}>legacy (not managed) · unencrypted .tar · QNAP</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    );
  };

  const startConfirm = (mode, dest) => {
    const lr = modeHist(mode)?.last_run || {};
    const fa = APPS[appOfMode(mode)];
    return (
      <ConfirmPanel ui={ui} busy={busy} onCancel={() => setConfirm(null)}
        title={`Start a ${MODE_TITLE[mode]} backup ${DEST_LABEL[dest]}?`}
        okText={`Start ${MODE_TITLE[mode]} ${DEST_LABEL[dest]}`} onOk={() => doStart(mode, dest)}>
        <div>What: <b>{MODE_TITLE[mode]}</b> — {MODE_SUB[mode]}. Where: <b>{DEST_WORDS[dest]}</b>.</div>
        <div style={{ marginTop: 4 }}>Size: {gib(lr.inner_bytes)} → {gib(lr.archive_bytes)} .7z {lr.estimate ? '(estimate)' : '(last run)'}</div>
        <div style={{ marginTop: 4 }}>Time: {timeEstimate(mode, dest)}</div>
        <div style={{ marginTop: 4 }}>
          Files land in: {dest === 'qnap' && fa.qnap}
          {dest === 'both' && <>{fa.qnap} and {fa.unas}</>}
          {dest === 'unas' && <>{fa.unas} — built on the QNAP temporarily, deleted after the UNAS copy is checked</>}
        </div>
        <div style={{ marginTop: 4 }}>🔒 Encrypted (AES-256 .7z, file names hidden{mode === 'mm' || mode === 'tc' || mode === 'cal' ? ', stored without compression, same password as the InsureDocs backups' : ''}){st?.archive?.pw_label ? ` · key ${st.archive.pw_label}` : ''}</div>
        {mode === 'cal' && (
          <div style={{ marginTop: 4 }}>
            The export is pulled from HDCal over SSH into local staging and checked before it is encrypted (every
            checksum against its MANIFEST, the Davis database dump complete, all required folders); the local copy is
            deleted right after. A start is refused while HDCal is busy with another export or the 23:00 nightly HDCal
            backup is due.
          </div>
        )}
        {mode === 'tc' && (
          <div style={{ marginTop: 4 }}>
            The export is pulled from timeclock-vm over SSH into local staging and checked before it is encrypted (both
            databases read with pg_restore, every checksum against MANIFEST.txt, all required folders); the local copy is
            deleted right after. A start is refused while timeclock-vm&apos;s own nightly backup is running.
          </div>
        )}
        {mode === 'mm' && (
          <div style={{ marginTop: 4 }}>
            The bundle is pulled from CT 1017 over SSH into local staging, checked (MANIFEST.txt, RESTORE.md, db/mattermost.dump …), then encrypted; the local copy is deleted right after.
            {blackout?.windows?.length ? ` Not available ${blackout.windows.map(w => w.replace('-', '–')).join(' and ')} (Central, CT 1017's own Proxmox backups); a start whose run would overlap one is refused.` : ''}
          </div>
        )}
        {retentionLines(mode, dest)}
        {mode === 'full' && (
          <div style={{ marginTop: 4, color: textSecondary }}>
            Only the archive phase has to stay clear of the nightly backups; the job refuses to start if one is due too soon.
          </div>
        )}
      </ConfirmPanel>
    );
  };

  const retryConfirm = () => {
    if (confirm?.kind !== 'retry') return null;
    const row = (modeHist(confirm.mode)?.sets || []).find(s => s.name === confirm.name);
    return (
      <ConfirmPanel ui={ui} busy={busy} onCancel={() => setConfirm(null)} title="Retry the UNAS upload?" okText="Retry upload to UNAS" onOk={() => doRetry(confirm.name)}>
        <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{confirm.name}</div>
        <div style={{ marginTop: 4 }}>
          Size {gib(row?.bytes)}. Uploads the copy that is on the QNAP{row?.staging ? ' (temporary folder; deleted after the UNAS copy is checked)' : ''} to the UNAS,
          checks it by sha256, then applies the UNAS retention. The QNAP set is never rewritten (only a missing .7z.sha256 is added).
        </div>
      </ConfirmPanel>
    );
  };

  // ── render ─────────────────────────────────────────────────────────────────
  const unknown = resp && resp.units_unknown;
  const refreshNeeded = isRefresh(actionErr);
  const mmPaused = !!blackout?.active;
  const cellBtn = (mode, dest) => {
    const sel = confirm?.kind === 'start' && confirm.mode === mode && confirm.dest === dest;
    const paused = mode === 'mm' && mmPaused;
    const dis = busy || !resp || running || paused;
    return (
      <button key={dest} disabled={dis} title={paused ? `Paused until ${blackout.until} (CT 1017 backup)` : undefined}
        onClick={() => { setActionErr(null); setConfirm({ kind: 'start', mode, dest }); }}
        style={{ ...btn(true, dis), padding: '9px 10px', background: sel ? '#1f2d3a' : '#34495e' }}>
        {DEST_LABEL[dest]}
      </button>
    );
  };
  const cfg = APPS[app];
  const ah = appHist(app);
  const segBtn = (a) => {
    const on = a === app;
    return (
      <button key={a} onClick={() => setApp(a)} aria-pressed={on}
        style={{
          padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
          background: on ? '#34495e' : 'transparent', color: on ? '#fff' : textSecondary,
        }}>
        {APPS[a].name}
      </button>
    );
  };
  return (
    <div style={{ background: panelBg, borderRadius: 8, padding: '10px 14px', marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>One-click encrypted backups</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, color: textSecondary }}>.7z AES-256 → QNAP / UNAS / both</span>
          <button onClick={() => { tick(); loadHistory(); }} disabled={histLoading}
            style={{ background: 'none', border: `1px solid ${borderColor}`, color: textSecondary, fontSize: 11, padding: '2px 8px', borderRadius: 4, cursor: histLoading ? 'wait' : 'pointer' }}>
            {histLoading ? '…' : '↻'}
          </button>
        </span>
      </div>

      {statusErr && <Box ui={ui} kind="red">Status unavailable: {statusErr}</Box>}
      {unknown && <Box ui={ui} kind="amber">Cannot ask systemd whether a backup is running — buttons may be refused.</Box>}
      {resp?.status_error && <Box ui={ui} kind="amber">{resp.status_error}</Box>}
      {refreshNeeded && (
        <Box ui={ui} kind="red"><b>InsureDocs was updated — please refresh the page (F5)</b> before starting a backup.</Box>
      )}

      {/* shared: the running job or the last result, whichever app (one job at a time) */}
      {running && runningView()}
      {!running && resultBox()}
      {retryConfirm()}

      {/* app switch */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
        <div style={{ display: 'inline-flex', border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
          {APP_ORDER.map(segBtn)}
        </div>
        {running && <span style={{ fontSize: 11, color: textSecondary }}>A backup is running — the start buttons are disabled.</span>}
      </div>

      {/* this app's modes × 3 destinations */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(170px, 1.6fr) repeat(3, 1fr)', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: textSecondary }}>{cfg.name}</div>
        {DESTS.map(d => <div key={d} style={{ fontSize: 11, color: textSecondary, textAlign: 'center' }}>{DEST_LABEL[d]}</div>)}
        {cfg.modes.map(mode => (
          <React.Fragment key={mode}>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600 }}>{MODE_ICON[mode]}{MODE_NAME[mode]}</div>
              <div style={{ fontSize: 11, color: textSecondary }}>{MODE_SUB[mode]}</div>
              <div style={{ fontSize: 11, color: textSecondary }}>{sizeLine(mode)}</div>
            </div>
            {DESTS.map(d => cellBtn(mode, d))}
          </React.Fragment>
        ))}
      </div>
      {app === 'mattermost' && blackout?.windows?.length > 0 && (
        mmPaused
          ? <Box ui={ui} kind="amber">⏸ Paused now: CT 1017's own Proxmox backup window {blackout.active.replace('-', '–')} (Central). Mattermost backups can start after {blackout.until}.</Box>
          : <div style={{ fontSize: 11, color: textSecondary, marginTop: 6 }}>
              Not available {blackout.windows.map(w => w.replace('-', '–')).join(' and ')} (Central) — CT 1017's own Proxmox backups. A start whose run would overlap one is refused.
            </div>
      )}

      {!running && confirm?.kind === 'start' && startConfirm(confirm.mode, confirm.dest)}
      {actionErr && !refreshNeeded && <Box ui={ui} kind="amber">{actionErr}</Box>}

      <div style={{ marginTop: 14, borderTop: `1px solid ${borderColor}`, paddingTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>Last runs — {cfg.name}</div>
        {histErr && <Box ui={ui} kind="red">History unavailable: {histErr}</Box>}
        {hist && ah && !ah.qnap_available && <Box ui={ui} kind="amber">QNAP folder not available: {ah.qnap_unavailable}</Box>}
        {hist && !ah && <div style={{ fontSize: 12, color: textSecondary, marginTop: 6 }}>No {cfg.name} history from the server — refresh the page (F5).</div>}
        {hist && ah && ah.qnap_available && cfg.modes.map(historyTable)}
        {!hist && !histErr && <div style={{ fontSize: 12, color: textSecondary, marginTop: 6 }}>Loading…</div>}
        {ah?.inventory && !ah.inventory.available && ah.qnap_available && (
          <div style={{ fontSize: 11, color: textSecondary, marginTop: 6 }}>UNAS list not written yet (appears after the first run that uses the UNAS) — UNAS-only sets show up from then on.</div>
        )}
        {ah?.inventory?.generated_at && <div style={{ fontSize: 11, color: textSecondary, marginTop: 6 }}>UNAS list as of {dateTime(ah.inventory.generated_at)} (Central; refreshed by every successful run that uses the UNAS).</div>}
      </div>
    </div>
  );
}
